import React, {
  CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';

import {
  AssistantChatScrollReversed,
  AssistantChatScrollReversedRef,
  FirstMessagePositioning,
  ChatViewer,
  ChatViewerHandle,
  followMessagesAtBottom,
} from 'assistant-chat-scroll';

import { useEnvironment, useExperiments } from '@wix/yoshi-flow-editor';
import { MessageType } from '@wix/ambassador-innovation-widget-v1-message/types';

import { messagesModel, addMessage, selectMessages } from '@/features';
import { useAppDispatch, useAppSelector } from '@/store';

import { useOnEditorSettingsEvent } from 'AiAssistantWidget/Widget/contexts';
import { settingsEvents } from 'AiAssistantWidget/settingsEvents';

import { Container } from '../Container';
import { Header } from '../Header';
import { Input } from '../Input';
import { ChatMessage } from '../ChatMessage';
import { Loader } from '../Loader';

import { st, classes, cssStates } from './Chat.st.css';
import { useHistoryLoader } from './useHistoryLoader';
import { useIntroSequence } from './useIntroSequence';

import './imports.global.scss';

const chatViewerStyle: CSSProperties = {
  width: '100%',
  height: '100%',
};

const scrollerStyle: CSSProperties = {
  boxSizing: 'border-box',
  paddingBottom: 12,
  paddingTop: 24,
};

export const Chat: React.FC = () => {
  const ref = useRef<AssistantChatScrollReversedRef | null>(null);
  const chatRef = useRef<ChatViewerHandle<messagesModel.Message>>(null);
  const parentRef = useRef<HTMLDivElement | null>(null);
  const dispatch = useAppDispatch();
  const messages = useAppSelector(selectMessages.visibleMessages);
  const isTyping = useAppSelector(selectMessages.isTyping);
  const isLoaded = useAppSelector(selectMessages.isLoaded);
  const isLoading = useAppSelector(selectMessages.isLoading);
  const loadingFirstPage = useAppSelector(selectMessages.loadingFirstPage);
  const messagesIdle = useAppSelector(selectMessages.isIdle);
  const loadOlderMessages = useHistoryLoader();
  const { isEditor } = useEnvironment();
  const { experiments } = useExperiments();
  const isNewChatViewerEnabled = experiments.enabled(
    'specs.aiAssistant.newChatViewer',
  );

  const isBusy = useMemo(
    () => isTyping || !messagesIdle,
    [isTyping, messagesIdle],
  );

  const { isSequenceFulfilled } = useIntroSequence();

  const [value, setValue] = React.useState('');

  const onInputChange = useCallback(
    (newValue: string) => {
      setValue(newValue);
      if (isNewChatViewerEnabled) {
        chatRef.current?.scrollToIndex(-1, { align: 'end', smooth: true });
      } else {
        // TODO: Remove this case when the new chat viewer is fully adopted
        ref.current?.scrollToIndex(-1, { align: 'start', animate: true });
      }
    },
    [isNewChatViewerEnabled],
  );

  const onSubmit = useCallback(() => {
    if (value.trim().length) {
      dispatch(addMessage({ body: { mainText: value } }));
      setValue('');
    }
  }, [dispatch, value]);

  useOnEditorSettingsEvent(
    settingsEvents.openAssistantMessages,
    useCallback(() => {
      if (!isEditor) {
        return;
      }
      ref.current?.scrollToIndex(1, { align: 'start', animate: true });
    }, [isEditor]),
  );

  useEffect(() => {
    if (isNewChatViewerEnabled) {
      if (isEditor) {
        chatRef.current?.scrollToIndex(0, { align: 'end', smooth: true });
      }
    } else {
      // TODO: Remove this case when the new chat viewer is fully adopted
      if (isEditor) {
        ref.current?.scrollToIndex(0, { align: 'start', animate: true });
      } else if (messages.length) {
        const lastMessage = messages[messages.length - 1];
        if (lastMessage.messageType === MessageType.CONTACT_FORM) {
          ref.current?.scrollToIndex(-1, { align: 'end', animate: true });
        }
      }
    }
  }, [isEditor, isNewChatViewerEnabled, messages]);

  const renderMessage = useCallback(
    (message: messagesModel.Message) => {
      return (
        <ChatMessage
          key={message.id}
          message={message}
          last={message.id === messages.at(-1)?.id}
        />
      );
    },
    [messages],
  );

  const onNewerMessages = useCallback(
    (
      chat: ChatViewerHandle<messagesModel.Message>,
      received: messagesModel.Message[],
    ) => {
      if (isEditor) {
        return chat.scrollToTop({ align: 'end', smooth: true });
      }

      const lastMessage = received.at(-1);
      const isContactForm =
        lastMessage?.messageType === MessageType.CONTACT_FORM;

      if (isContactForm) {
        return chat.scrollToId(lastMessage.id, { align: 'start' });
      }

      return followMessagesAtBottom<messagesModel.Message>()(chat, received);
    },
    [isEditor],
  );

  const viewer = useMemo(() => {
    if (isNewChatViewerEnabled) {
      return (
        <ChatViewer<messagesModel.Message>
          ref={chatRef}
          alignment="top"
          style={chatViewerStyle}
          scrollerStyle={scrollerStyle}
          overscan={10}
          historyEndOffset={5}
          onHistoryEndReached={loadOlderMessages}
          prefix={isLoading && !isEditor && <Loader />}
          suffix={isTyping && !isEditor && <Loader />}
          messages={messages}
          renderMessage={renderMessage}
          onNewerMessages={onNewerMessages}
        />
      );
    }

    return (
      <AssistantChatScrollReversed
        ref={ref}
        paddingStart={24}
        paddingStartFirstMessage={24}
        paddingEnd={12}
        estimateSizePx={50}
        scrollLoadHistoryOffsetPx={5}
        overscan={10}
        style={chatViewerStyle}
        scrollLoadHistoryWorker={loadOlderMessages}
        loader={<Loader />}
        scrollToLastOnAppend={!isEditor}
        firstMessagePosition={FirstMessagePositioning.start}
      >
        {messages.map((m, i) => (
          <ChatMessage
            message={m}
            last={i === messages.length - 1}
            key={m.id}
          />
        ))}
        {isTyping && <Loader />}
      </AssistantChatScrollReversed>
    );
  }, [
    isNewChatViewerEnabled,
    isEditor,
    isLoading,
    isTyping,
    loadOlderMessages,
    messages,
    onNewerMessages,
    renderMessage,
  ]);

  return (
    <Container ariaBusy={isBusy}>
      <Header />
      <div ref={parentRef} className={st(classes.container)}>
        {loadingFirstPage && <Loader />}
        <div
          className={st(classes.wrapper, cssStates({ isVisible: isLoaded }))}
        >
          {viewer}
        </div>
      </div>
      <Input
        value={value}
        onChange={onInputChange}
        onSubmit={onSubmit}
        disabled={!isSequenceFulfilled}
      />
    </Container>
  );
};
